import { useEffect, useRef } from "react";
import { UseFormGetValues, UseFormReset, UseFormSetValue } from "react-hook-form";
import { IWasteForm } from "../components/wasteManagment/wasteManagement.types";
import {
  planWasteFormSync,
  WASTE_FORM_SYNC_INIT_SIGNATURE,
  WasteFormSyncPlan
} from "./wasteFormSync.utils";

interface UseWasteFormSyncParams {
  wasteManagement: any[];
  accordionEnabled: boolean;
  sideStreamValue: any;
  defaultTypeId: string;
  reset: UseFormReset<IWasteForm>;
  setValue: UseFormSetValue<IWasteForm>;
  getValues: UseFormGetValues<IWasteForm>;
  setEditingId: (id: string | number | null) => void;
  parentControl?: { getValues?: (name: string) => unknown };
  parentSideStreamUnit?: number | string;
}

interface FormSyncActions {
  setValue: UseFormSetValue<IWasteForm>;
  reset: UseFormReset<IWasteForm>;
  setEditingId: (id: string | number | null) => void;
}

const setValueOptions = { shouldDirty: false, shouldValidate: false } as const;

/**
 * Applies a waste form sync plan by calling React Hook Form setters.
 *
 * @param plan - Sync plan from planWasteFormSync
 * @param actions - Form mutation callbacks (setValue, reset, setEditingId)
 */
function applyWasteFormSyncPlan(plan: WasteFormSyncPlan, actions: FormSyncActions): void {
  switch (plan.action) {
    case "noop":
      return;
    case "patch":
      actions.setValue("sideStream", plan.sideStream, setValueOptions);
      if (plan.sideStreamUnit !== null) {
        actions.setValue("sideStreamUnit", plan.sideStreamUnit, setValueOptions);
      }
      return;
    case "reset":
      actions.reset(plan.formState);
      actions.setEditingId(null);
  }
}

export const useWasteFormSync = ({
  wasteManagement,
  accordionEnabled,
  sideStreamValue,
  defaultTypeId,
  reset,
  setValue,
  getValues,
  setEditingId,
  parentControl,
  parentSideStreamUnit
}: UseWasteFormSyncParams) => {
  const lastRecordSignatureRef = useRef(WASTE_FORM_SYNC_INIT_SIGNATURE);

  useEffect(() => {
    const plan = planWasteFormSync({
      accordionEnabled,
      existingRecords: wasteManagement || [],
      sideStreamValue,
      defaultTypeId,
      parentSideStreamUnit: parentControl ? parentSideStreamUnit : undefined,
      lastRecordSignature: lastRecordSignatureRef.current,
      currentSideStreamUnit: getValues("sideStreamUnit")
    });

    lastRecordSignatureRef.current = plan.nextSignature;
    applyWasteFormSyncPlan(plan, { setValue, reset, setEditingId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasteManagement, accordionEnabled, sideStreamValue, parentSideStreamUnit]);
};
